import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { PageGallery } from "@/components/PageGallery";
import { SiteFooter } from "@/components/SiteFooter";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { NOTARY_KEYWORDS_TOP as NOTARY_KEYWORDS } from "@/content/notary-corpus/top.gen";
import type { FaqItem } from "@/content/notary-corpus/keywords";
import { NOTARY_FAQ_CATEGORIES, getCategoryDef } from "@/content/notary-corpus/faq-defs";

export const Route = createFileRoute("/notary/faq/$category")({
  loader: async ({ params }) => {
    const { getFaqsByCategoryTh } = await import("@/content/notary-corpus/categories-th");
    const def = getCategoryDef(params.category);
    if (!def) throw notFound();
    return { def, faqs: getFaqsByCategoryTh(def.slug) };
  },
  component: NotaryFaqCategoryPage,
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "หมวดคำถามไม่พบ" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const site = getActiveSiteUrl();
    const { def, faqs } = loaderData;
    const title = `${def.titleTh} — Notary Public FAQ ${faqs.length} ข้อ (2026)`;
    return {
      ...pageHeadForProvince({
        title,
        description: def.descTh,
        path: `/notary/faq/${params.category}`,
        type: "article",
        alternates: [{ lang: "en", path: `/en/notary/faq/${params.category}` }],
        keywords: NOTARY_KEYWORDS.slice(0, 60),
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "Notary FAQ", url: `${site}/notary/faq` },
              { name: def.titleTh, url: `${site}/notary/faq/${params.category}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2", ".faq-answer"] },
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/notary/faq/${params.category}`)) },
      ],
    };
  },
});

function NotaryFaqCategoryPage() {
  const { def, faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span>{" "}
          <a href="/notary/faq" className="hover:underline">Notary FAQ</a> <span>/</span>{" "}
          <span>{def.titleTh}</span>
        </nav>
        <h1 className="text-3xl md:text-4xl font-bold mb-3">{def.titleTh}</h1>
        <p className="text-muted-foreground mb-6">{def.descTh}</p>
        <p className="text-sm text-muted-foreground mb-8">
          รวม <strong>{faqs.length}</strong> คำถามในหมวดนี้ — ตอบโดยทนายผู้รับมอบอำนาจจากสภาทนายความ อัปเดต 2026
        </p>

        <div className="mb-8 flex flex-wrap gap-2 text-xs">
          {NOTARY_FAQ_CATEGORIES.map((c) => (
            <a
              key={c.slug}
              href={`/notary/faq/${c.slug}`}
              className={`rounded-full border px-3 py-1 ${c.slug === def.slug ? "bg-primary text-primary-foreground" : "bg-muted/40 text-muted-foreground hover:bg-muted"}`}
            >
              {c.titleTh}
            </a>
          ))}
        </div>

        <div className="space-y-4">
          {faqs.map((item: FaqItem, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4 open:shadow-sm" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="text-muted-foreground mr-2">Q{i + 1}.</span>
                {item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
          {faqs.length === 0 && (
            <p className="rounded-lg border bg-muted/30 p-6 text-sm text-muted-foreground">
              ยังไม่มีคำถามในหมวดนี้ — <a href="/contact" className="text-primary underline">สอบถามทีมงาน</a>
            </p>
          )}
        </div>

        <section className="mt-12 rounded-lg border p-6 bg-primary/5">
          <h2 className="text-xl font-bold mb-2">ปรึกษาฟรีกับทนายผู้รับมอบอำนาจ</h2>
          <p className="text-sm text-muted-foreground mb-4">ตอบใน 15 นาที (จ.-ส. 9:00-18:00)</p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 text-primary-foreground font-medium">ติดต่อทีมงาน →</a>
        </section>
      
        <PageGallery category="notary" seed="notary-faq-category" locale="th" />
        <FaqSourceNote topic="notary" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}